import {
	LayoutRoot,
	LayoutSideBar,
	LayoutSideBarFooter,
	LayoutSideBarHeader,
	LayoutTopBar,
	SidebarStatusActionTypes,
} from "@moznsystems/mozn-ui";
import { logoHorizontal, logoWordMark } from "@rageeb/frontend-lib";
import { type FC, type PropsWithChildren, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useToast } from "@/components/toast";
import { IS_SELF_HOSTED } from "@/constants/constants";
import { LocalizedNavLink } from "@/router/localized-link";
import { setErrorHandler } from "@/services/api";
import { useSidebarStatusContext } from "@/store/sidebar-status-context";
import { AccountPreview } from "./AccountPreview";
import AppSwitcher from "./AppSwitcher";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { SideBar } from "./SideBar/side-bar";

const Layout: FC<PropsWithChildren> = ({ children }) => {
	const { sidebarStatus, handleSidebarStatus } = useSidebarStatusContext();
	const [isDrawerVisible, setDrawerVisible] = useState<boolean>();
	const toast: any = useToast();
	const { t } = useTranslation("layout");

	useEffect(() => {
		setErrorHandler(toast.pushError);
	}, []);

	return (
		<LayoutRoot
			isSideBarClose={sidebarStatus === SidebarStatusActionTypes.CLOSE_SIDEBAR}
		>
			<LayoutSideBar
				sidebarStatus={sidebarStatus}
				handleSidebarStatus={handleSidebarStatus}
				isDrawerVisible={isDrawerVisible}
				handleDrawerVisible={(value) => setDrawerVisible(value)}
			>
				<LayoutSideBarHeader>
					{IS_SELF_HOSTED ? null : <AppSwitcher />}
					<LocalizedNavLink
						to="/"
						className="logo"
						aria-label={t("logo.brand_aria")}
					>
						<div
							className={
								IS_SELF_HOSTED
									? "logo__container"
									: "logo__container logo__container--with-app-switcher"
							}
						>
							<img
								className="logo__container__horizontal"
								src={logoWordMark}
								alt={t("logo.alt_focal")}
							/>
							<img
								className="logo__container__mini"
								src={logoWordMark}
								alt={t("logo.alt_focal")}
							/>
							<p className="logo__container__text">
								<span>{t("logo.brand_suffix")}</span>
							</p>
						</div>
					</LocalizedNavLink>
				</LayoutSideBarHeader>
				<SideBar setDrawerVisible={(value) => setDrawerVisible(value)} />
				<LayoutSideBarFooter>
					<div className="powered_by">
						<img src={logoHorizontal} alt={t("logo.alt_mozn")} />
					</div>
				</LayoutSideBarFooter>
			</LayoutSideBar>
			{/*
			 * mozn-ui's `.top-bar` is absolutely positioned with no z-index, so the
			 * dropdowns inside it stack against the root and lose to sticky in-page
			 * headers (session details is z-30). z-40 keeps them on top while staying
			 * under overlays, modals and toasts (z-1040+). `top-bar` is repeated
			 * because LayoutTopBar spreads props over its own className.
			 */}
			<LayoutTopBar className="top-bar z-40">
				<LanguageSwitcher />
				<AccountPreview />
			</LayoutTopBar>
			{children}
		</LayoutRoot>
	);
};

export { Layout };
